/** Fetch client for the HQ Worker. Cross-origin with credentials (SameSite=None cookie). */
import type { AssetToken } from '@shared/types';
import { isSessionExpiry } from './sessionExpiry';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

// Session is a Bearer token in localStorage (robust cross-site; no third-party cookie).
let token: string | null = null;
try {
  token = localStorage.getItem('hq_token');
} catch {
  /* ignore */
}
export function setToken(t: string): void {
  token = t;
  sessionExpiryHandled = false; // a fresh session can detect its own future expiry
  try {
    localStorage.setItem('hq_token', t);
  } catch {
    /* ignore */
  }
}
export function clearToken(): void {
  token = null;
  try {
    localStorage.removeItem('hq_token');
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

/* ---------- session-expiry handling ----------
   Sessions are 30-day JWTs. When one expires (or is otherwise invalidated) while
   the app is open, every guarded call starts returning 401. Without a global
   handler the founder just sees a silently broken app with no way back. So a 401
   that means "your session is gone" clears the token and bounces to /login. The
   pure "is this a session death vs. a bad-password check?" decision lives in
   ./sessionExpiry (imported above) so it can be unit-tested without this
   browser-only module. */
let sessionExpiryHandled = false; // one bounce even if many calls 401 at once

/** Clear the dead session and signal the app to bounce to /login. App listens for
 *  'hq:session-expired'; Login reads the flag to explain why they're back here. */
function handleSessionExpiry(): void {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  clearToken();
  try { sessionStorage.setItem('hq_session_expired', '1'); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event('hq:session-expired')); } catch { /* ignore */ }
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', ...((opts.headers as Record<string, string>) ?? {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { credentials: 'include', ...opts, headers });
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    // Only force-logout when we *thought* we had a session — a bare 401 with no
    // token is just an unauthenticated call the router already handles.
    if (token && isSessionExpiry(res.status, path)) handleSessionExpiry();
    throw new ApiError(res.status, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => req<T>(path),
  post: <T>(path: string, body?: unknown) => req<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) => req<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string, body?: unknown) => req<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
  base: BASE,
};

/* ---------- asset auth ----------
   <img>/<video>/<audio> tags can't send the Bearer header, so R2 assets are
   served with a short-lived query token: GET /auth/asset-token → {token, exp}
   (15-min JWT, scope 'assets'). Fetched once and cached until 60s before exp. */
let assetToken: AssetToken | null = null;
let assetTokenInFlight: Promise<AssetToken | null> | null = null;
let assetTokenRetryAt = 0; // backoff so a missing endpoint isn't hammered

async function getAssetToken(): Promise<string | null> {
  const nowSec = Date.now() / 1000;
  if (assetToken && assetToken.exp - 60 > nowSec) return assetToken.token;
  if (!assetTokenInFlight) {
    if (Date.now() < assetTokenRetryAt) return null;
    assetTokenInFlight = req<AssetToken>('/auth/asset-token')
      .then((t) => {
        assetToken = t && typeof t.token === 'string' ? t : null;
        return assetToken;
      })
      .catch(() => {
        // Endpoint unreachable (or not deployed yet) — back off for 30s.
        assetTokenRetryAt = Date.now() + 30_000;
        return null;
      })
      .finally(() => {
        assetTokenInFlight = null;
      });
  }
  const t = await assetTokenInFlight;
  return t ? t.token : null;
}

/** Tokened URL for an R2 asset key; falls back to the bare URL if no token. */
export async function assetUrl(key: string): Promise<string> {
  const t = await getAssetToken();
  // Encode each path segment (keys may contain '?', '#', '%', …) but keep '/' separators.
  const safeKey = key.split('/').map(encodeURIComponent).join('/');
  const bare = `${BASE}/assets/${safeKey}`;
  return t ? `${bare}?t=${encodeURIComponent(t)}` : bare;
}
